import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { DeliveryOrdersTabs } from './delivery-orders-tabs';
import Toggle from 'rsuite/Toggle';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  CleanDeliveryMethod,
  CleanDeliveryOrder,
  CleanLocalDeliveryMethod,
  RbacObject,
} from '../../../export-types/cleaned-types';
import { Rbac, useRbac } from '../../common/Rbac';
import {
  SupplierSelect,
  useSelectSupplierQs,
} from '../purchase-order/supplier-select';
import { useSearchParamsData } from '../purchase-order/useSearchParamsData';

import { useSortingLocalStorage } from '../../../api/useSortingLocalStorage';
import { LocalDeliveryMethodSelect } from '../../common/local-delivery-method-select';
import { DeliveryMethodSelect } from '../../common/delivery-method-select';
import { DeliveryOrdersScreenInTransitBlock } from './delivery-orders-screen-in-transit-block';
import { userIsAdmin } from '../../../utils/common.utils';
import { useProfile } from '../../../api/profile';
import { Button } from 'rsuite';
import { ModalsService } from '../../../services/modals.service';
import { DeliveriesReportModal } from './sent-to-russia-tab/deliveries-report-modal';

export type OutletContextParams = {
  debouncedInternalId: string;
  linked: boolean;
  attention: boolean;
  expanded: boolean;
  collapsed: string[];
  onChangeCollapsed: (hidden: string[]) => void;
  selectSupplier: number | undefined;
  direction?: string;
  selectedOrders: CleanDeliveryOrder[];
  onChangeSelectedOrders: (selected: CleanDeliveryOrder[]) => void;
  selectAllOrders: boolean;
  setSelectAllOrders: (value: boolean) => void;
  selectedLocalMethods: CleanLocalDeliveryMethod[];
  selectedMethods: CleanDeliveryMethod[];
};

export const DeliveryOrdersScreen = () => {
  const {
    sortValues,
    toggleExpanded,
    toggleLinked,
    toggleDirection,
    toggleAttention,
  } = useSortingLocalStorage();

  const location = useLocation();
  const paths = location.pathname.split('/');
  const { data: profile } = useProfile();
  const { hasPermission } = useRbac();

  const [searchParamsData, setSearchParamsFunctions] = useSearchParamsData();

  const [internalId, setInternalId] = useState(
    searchParamsData['internal'] || '',
  );

  const [collapsed, setCollapsed] = React.useState<string[]>([]);
  const [supplierId] = useSelectSupplierQs();
  const [selectedOrders, setSelectedOrders] = useState<CleanDeliveryOrder[]>(
    [],
  );
  const [selectAllOrders, setSelectAllOrders] = useState(false);
  const [selectedLocalMethods, setSelectedLocalMethods] = React.useState<
    CleanLocalDeliveryMethod[]
  >([]);
  const [selectedMethods, setSelectedMethods] = React.useState<
    CleanDeliveryMethod[]
  >([]);

  useEffect(() => {
    if (searchParamsData['internal'] !== internalId) {
      setSearchParamsFunctions['internal'](internalId);
    }
  }, [internalId, setSearchParamsFunctions]);

  const intl = useIntl();

  const isWarehouseRussia =
    userIsAdmin(profile) ||
    hasPermission(RbacObject.DeliveryOrder, 'read:warehouse-russia');

  const context: OutletContextParams = {
    debouncedInternalId: internalId,
    linked: sortValues.linked,
    attention: sortValues.attention,
    expanded: sortValues.expanded ?? true,
    collapsed,
    onChangeCollapsed: setCollapsed,
    selectSupplier: supplierId,
    direction: sortValues.direction,
    selectedOrders,
    onChangeSelectedOrders: setSelectedOrders,
    selectAllOrders,
    setSelectAllOrders,
    selectedLocalMethods,
    selectedMethods,
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pb-4 ">
        <div className="d-flex mr-auto " style={{ width: '80%' }}>
          <input
            className="form-control"
            style={{ width: '20%' }}
            type="text"
            placeholder={intl.formatMessage({
              defaultMessage: 'Поиск',
              id: 'input.placeholder.searchByOrder/CustomerNumber',
              description: 'Поле ввода',
            })}
            value={searchParamsData['internal']}
            onChange={(event) => setInternalId(event.currentTarget.value)}
          />
          <Rbac object={RbacObject.Supplier} action={'read:admin'}>
            <SupplierSelect />
          </Rbac>
          {['sent-to-russia', 'received-in-russia'].includes(paths?.[3]) &&
            isWarehouseRussia && (
              <>
                <div className={'ms-2'}>
                  <LocalDeliveryMethodSelect
                    supplierId={supplierId}
                    value={selectedLocalMethods}
                    onChange={(data) => {
                      setSelectedLocalMethods(data);
                    }}
                    placeholder={'Транс. компания'}
                  />
                </div>
                <div className={'ms-2'}>
                  <DeliveryMethodSelect
                    value={selectedMethods}
                    onChange={(data) => {
                      setSelectedMethods(data);
                    }}
                    placeholder={'Способ доставки'}
                  />
                </div>
                <div className={'my-auto ms-2'}>
                  <Button
                    className={'btn btn-success'}
                    onClick={() => {
                      ModalsService.createModal(DeliveriesReportModal);
                    }}
                  >
                    {intl.formatMessage({
                      id: 'deliveryOrder.deliveriesData',
                      defaultMessage: 'Данные по доставкам',
                      description: 'Надпись на вкладке',
                    })}
                  </Button>
                </div>
              </>
            )}
        </div>
        <div className="ms-1 d-flex align-items-center">
          <Toggle checked={sortValues.attention} onChange={toggleAttention} />
          <div className="ms-2">
            <FormattedMessage
              defaultMessage="Требуют внимания"
              id="switchButton.label.attention"
              description="Надпись на переключателе"
            />
          </div>
        </div>

        <div className="d-flex align-items-center">
          <Toggle checked={sortValues.linked} onChange={toggleLinked} />
          <div className="ms-2">
            <FormattedMessage
              defaultMessage="Мои клиенты"
              id="switchButton.label.myCustomers"
              description="Надпись на переключателе"
            />
          </div>
        </div>
        <div className="d-flex align-items-center">
          <Toggle
            checked={sortValues.direction === 'desc'}
            onChange={toggleDirection}
          />
          <div className="ms-2">
            <FormattedMessage
              defaultMessage="Сначала новые"
              id="switchButton.label.newOnesFirst"
              description="Надпись на переключателе"
            />
          </div>
        </div>
        <div className="d-flex ms-n3 align-items-center">
          <button
            className="btn d-flex align-items-center"
            onClick={() => {
              toggleExpanded();
              setCollapsed([]);
            }}
          >
            {sortValues.expanded ? (
              <>
                <i className="bi bi-chevron-down me-1"></i>
                <FormattedMessage
                  defaultMessage="Показать заказы"
                  id="switchButton.label.showOrders"
                  description="Надпись на переключателе"
                />
              </>
            ) : (
              <>
                <i className="bi bi-chevron-up me-1"></i>
                <FormattedMessage
                  defaultMessage="Cкрыть заказы"
                  id="switchButton.label.hideOrders"
                  description="Надпись на переключателе"
                />
              </>
            )}
          </button>
        </div>
      </div>
      <div className="mb-4 d-flex flex-row gap-2">
        <DeliveryOrdersTabs
          selectSupplier={supplierId}
          debouncedInternalId={internalId}
          linked={sortValues.linked}
          attention={sortValues.attention}
          expanded={sortValues.expanded ?? true}
          collapsed={collapsed}
          onChangeCollapsed={setCollapsed}
          selectedOrders={selectedOrders}
          onChangeSelectedOrders={setSelectedOrders}
          selectAllOrders={selectAllOrders}
          setSelectAllOrders={setSelectAllOrders}
          selectedLocalMethods={selectedLocalMethods}
          selectedMethods={selectedMethods}
        />
        {paths?.[3] === 'sent-to-russia' && isWarehouseRussia ? (
          <DeliveryOrdersScreenInTransitBlock
            selectAllOrders={selectAllOrders}
            setSelectAllOrders={setSelectAllOrders}
            selectedOrders={selectedOrders}
          />
        ) : null}
      </div>
      <Outlet context={context} />
    </>
  );
};
